import ViewController from "@/interfaces/ViewController";
import {computed, Ref, ref, watch} from "vue";
// @ts-ignore
import AppController from '@target/components/App/ts/AppController';
import WithdrawalService, {createWithdrawalPayload} from "@/services/operations/withdrawal/WithdrawalService";
import Requisite from "@models/requisite/Requisite";
import AgentsAmountRanges from "@models/errors/components/AgentsAmountRanges";
import ServerResponseError from "@/errors/ServerResponseError";
import ModelRates from "@models/v2/rates/ModelRates";
import ServiceRates from "@/services/v2/data/service-rates/ServiceRates";
import { FirebaseService } from "@/services/firebase/FirebaseService";
import { DocumentTypes } from "@/services/firebase/firestore/documents/DocumentTypes";
import DocumentWithdrawal from "@/services/firebase/firestore/documents/DocumentWithdrawal";
import ModelWarningBlockNotification from "@models/v2/firebase/warning-blocks/ModelWarningBlockNotification";
import { ComputedRef } from "vue";
import {ServiceWarningBlocks} from "@/services/v2/service-warning-blocks/ServiceWarningBlocks";
import ServiceMainApi from "@/services/v2/service-main-api/ServiceMainApi";
import {RequisitePsystemTargetType} from "@enums/RequisitePsystemTargetType";
import {ServiceAddressTypes} from "@/services/v2/data/service-address-types/ServiceAddressTypes";
import PaymentSystem from "@/entities/PaymentSystem";
import ServiceAccount from "@/services/v2/data/service-account/ServiceAccount";
import ModelAccount from "@models/v2/account/ModelAccount";
import ServiceRanges from "@/services/v2/data/service-ranges/ServiceRanges";
import RouterService from "@/services/RouterService";

interface RequisitePayload {
    psystem: PaymentSystem | null;
    name: string;
    address: string;
    addressTypeId: number;
    comment: string;
    isDefault: boolean;
    id: number | null;
}

const RequisitePayloadDefault: RequisitePayload = {
    psystem: {
        "id": 6,
        "name": "Любой Банк",
        "sysName": "otherbank",
        "addressTypes": [1, 2],
        "imgPath": `${import.meta.env.VITE_API_URL}/storage/psystems/thumb/LPZTPpvXyYgVshvg7C7ZNJC4mZMJsgD9SbiF1j6c.png`
    },
    name: '',
    address: '',
    addressTypeId: 1,
    comment: '',
    isDefault: true,
    id: null
}

export default class WithdrawalPopupController implements ViewController {
    private static instance?: WithdrawalPopupController;
    private _requisitePayload: Ref<RequisitePayload> = ref({...RequisitePayloadDefault});
    public defaultAmount: number = Number.parseInt(import.meta.env.VITE_WITHDRAWAL_DEFAULT_AMOUNT);
    private _minWalletAmount = ref<number>(Number.parseInt(import.meta.env.VITE_WITHDRAWAL_MIN_WALLET_AMOUNT));
    private _minP2PAmount = ref<number>(Number.parseInt(import.meta.env.VITE_WITHDRAWAL_MIN_P2P_AMOUNT));
    private _activeRequisiteAmountRange: Ref<AgentsAmountRanges | null> = ref(null);
    private _activeRequisite: Ref<Requisite | null> = ref(null);
    private _warningBlocks: ComputedRef<ModelWarningBlockNotification[]>;
    private _serviceWarningBlocks: ServiceWarningBlocks;
    private _serviceRanges: ServiceRanges;
    private _routerService: RouterService = AppController.getInstance().routerService;

    public account = ServiceAccount.of().account as Ref<ModelAccount>;
    public amount: Ref<number>;
    public rates = ServiceRates.of().rates as Ref<ModelRates>;
    public currentAmountCurrency = ref<"RUB" | "USD" | "USDT" | "KZT">("USD");
    public textMaxMinError = ref<boolean>(false);
    public textRequisiteError = ref<boolean>(false);
    public activeRequisiteMinMaxLoading = ref(false);
    public isSubmitLoading = ref(false);
    public inputAmountFocused = ref(false);
    public minMaxRangesAmountByRequisite: ComputedRef<{ min: number, max: number }>;
    public isNotEnoughFunds: ComputedRef<boolean>;
    public isMaxLessMinRates: ComputedRef<boolean>;
    public notEnoughFundsMinAmount: ComputedRef<number>;
    public isP2PRequisite = computed(() => this.activeRequisite.value?.psystem.targetType === RequisitePsystemTargetType.P2P);

    private constructor() {
        this._serviceWarningBlocks = ServiceWarningBlocks.of();
        this._serviceRanges = ServiceRanges.of();

        this._warningBlocks = computed(() => this._serviceWarningBlocks.warningBlocks.value?.withdrawalCreatePage?.filter(n => n.isActive) ?? []);
        AppController.getInstance().setPaymentRequestData(null);

        this.setWithdrawalFirebaseConfig();

        this.rates = ServiceRates.of().rates as Ref<ModelRates>;

        if (this._activeRequisiteAmountRange.value) {
            const {min, max} = this._activeRequisiteAmountRange.value as { min: number, max: number};

            if (max !== 0) {
                this.defaultAmount = +(min * this.rates.value.sell).toFixed(0);
            }
        }

        this.amount = ref<number>(this.defaultAmount);
        this.minMaxRangesAmountByRequisite = computed(() => {
            if (this._activeRequisiteAmountRange.value === null) return { min: 0, max: 0 };

            let { min, max } = this._activeRequisiteAmountRange.value;

            if (!min) min = 0;
            if (!max) max = 0;

            return { min, max };
        });

        this.isMaxLessMinRates = computed(() => {
            if (this._activeRequisiteAmountRange.value === null) return false;
            const { min, max } = this.minMaxRangesAmountByRequisite.value;
            return (!!min === !!max) && max <= min;
        });

        this.isNotEnoughFunds =computed(() => {
            if (this._activeRequisiteAmountRange.value === null) return false;
            return this.account.value.balance.amount < this.minMaxRangesAmountByRequisite.value.min;
        });

        this.notEnoughFundsMinAmount = computed(() => {
            if (this._activeRequisite.value && this._activeRequisite.value.getType() === 'wallet') {
                return this._minWalletAmount.value;
            } else {
                return this._minP2PAmount.value;
            }
        });
    }

    public checkRequisiteMinMaxAndSetInput = () => {
        const { min, max } = this.minMaxRangesAmountByRequisite.value;

        if (min && this.amount.value < min) {
            this.amount.value = min;
        }

        if (max && this.amount.value > max) {
            this.amount.value = max;
        }
    }

    public async fetchAddressTypes() {
        await ServiceAddressTypes.of().fetchAddressTypes();
    }

    public initRequisitePayload() {
        this._requisitePayload.value = {...RequisitePayloadDefault};
    }

    get warningBlocks(): ComputedRef<ModelWarningBlockNotification[]> {
        return this._warningBlocks;
    }

    get activeRequisite() {
        return this._activeRequisite;
    }

    get activeRequisiteAmountRange() {
        return this._activeRequisiteAmountRange;
    }

    get routerService(): RouterService {
        return this._routerService;
    }

    public setWithdrawalFirebaseConfig() {
        const doc = FirebaseService.of().firestoreService?.getDocument<DocumentWithdrawal>(DocumentTypes.WITHDRAWAL);

        if (doc) {
            if (doc.defaultAmount) {
                this.defaultAmount = doc.defaultAmount;
            }

            if (doc.minP2PAmount !== null) {
                this._minP2PAmount.value = doc.minP2PAmount;
            }

            if (doc.minWalletAmount !== null) {
                this._minWalletAmount.value = doc.minWalletAmount;
            }
        }
    }

    public async create2P2Withdrawal() {
        const {min, max} = this._activeRequisiteAmountRange.value!;

        const payload: createWithdrawalPayload = {
            requisite_id: this.activeRequisite.value!.id,
            amount: this.amount.value,
        };

        if (max === 0) {
            throw new ServerResponseError("Something went wrong", 70401);
        }

        const withdrawalService: WithdrawalService = AppController.getInstance().withdrawalService;
        AppController.getInstance().setPaymentRequestData({...payload, type: 'sail'});

        await withdrawalService.createWithdrawal(payload);
    }

    static getInstance() {
        if (typeof this.instance === 'undefined') this.instance = new WithdrawalPopupController();
        return this.instance;
    }

    public async fetchRequisiteMinMax(requisite: Requisite) {
        const ranges = await this._serviceRanges.fetchWithdrawalRanges(requisite);
        this._activeRequisiteAmountRange.value = this.calculateMinMaxAmountRangesByFirebase(ranges);
    }

    private calculateMinMaxAmountRangesByFirebase(amountRanges: AgentsAmountRanges): AgentsAmountRanges {
        this.setWithdrawalFirebaseConfig();
        if (!this._activeRequisite.value) return amountRanges;

        const isWallet = this._activeRequisite.value?.psystem.targetType === RequisitePsystemTargetType.WALLET;
        const minMaxDocAmountRangeByType = isWallet ? this._minWalletAmount.value : this._minP2PAmount.value;

        if (minMaxDocAmountRangeByType === null) return amountRanges;

        amountRanges.min = Math.max(amountRanges.min!, minMaxDocAmountRangeByType);

        return amountRanges;
    }

    public selectActiveRequisite(requisite: Requisite) {
        this._activeRequisite.value = requisite;
    }

    destructor(): void {
        WithdrawalPopupController.instance = undefined;
    }
}
